import type { TowerDesign } from "@/game/towerSpec";

/**
 * Self-supporting 4-legged lattice tower (à la ROHN SSV). Base width 5.6 m at
 * 40 m tall → base/height ≈ 1/7.1, squarely inside the real 1/6–1/12 range for
 * self-supporting towers (docs/ENGINEERING.md §3.2, ROHN SSV catalog). Owned by
 * the lattice agent.
 *
 * Rationale:
 *  - 12 panels → 3.33 m panel height: short enough that the 4" Sch-40 pipe legs
 *    (I = 7.23 in⁴) buckle at Pcr ≈ 535 kN, comfortably above the ~325 kN
 *    windward-leg force at the 50 m/s design gust. Yield cap (707 kN) is higher
 *    still, so the failure mode is leg buckling, not yield — realistic.
 *  - Heavy taper (baseR 2.8 → topR 1.0) mirrors real SSV geometry and drops the
 *    top-panel steel/wind.
 *  - Peak leg utilization: ~3% dead-only, ~40% at 40 m/s (TIA design wind),
 *    climbing to ~85% near 60 m/s where it fails plausibly (windward leg buckles
 *    → topple). Lots of standing headroom, plausible high-wind failure.
 */
export const design: TowerDesign = {
  id: "lattice-40m",
  name: "Lattice",
  description: "40 m self-supporting 4-legged X-braced tower (base/height ≈ 1/7).",
  spec: {
    type: "lattice",
    height: 40,
    stack: {
      faces: 4,
      panels: 12, // 3.33 m panels — legs stay stocky enough to resist buckling
      baseRadius: 2.8, // base width 5.6 m ≈ height/7.1 (real SSV proportion)
      topRadius: 1.0,
      legSection: "legHeavy", // 4" Sch-40 pipe — Pcr ≈ 535 kN per panel
      braceSection: "braceMed", // L3×3×1/4 angle bracing
      legMaterial: "a572", // A572 Gr50, modern tower steel (Fy 345 MPa)
      bracing: "X",
    },
  },
};
